import { History, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/common/loader";
import {
  createCaseNote,
  deleteCaseNote,
  listCaseNotes,
  updateCaseNote,
} from "@/features/cases/api";
import type { CaseNote } from "@/features/cases/types";
import { cn } from "@/lib/utils";

function formatWhen(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Notes / activity panel for the case detail page. Add a note, pin/unpin, edit
 * and delete user notes; read-only audit entries (case opened, status changed,
 * step completed) render inline with an activity marker and no controls.
 * `onActivity` bumps the parent so header counts stay consistent.
 */
export function CaseNotesPanel({
  caseId,
  onActivity,
}: {
  caseId: string;
  onActivity?: () => void;
}) {
  const [notes, setNotes] = useState<CaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleting, setDeleting] = useState<CaseNote | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listCaseNotes(caseId)
      .then((result) => active && setNotes(result))
      .catch(() => active && toast.error("Couldn't load notes."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [caseId]);

  function refetch() {
    return listCaseNotes(caseId)
      .then(setNotes)
      .catch(() => toast.error("Couldn't load notes."));
  }

  async function addNote() {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await createCaseNote(caseId, { text });
      setDraft("");
      await refetch();
      onActivity?.();
    } catch {
      toast.error("Couldn't add note.");
    } finally {
      setSaving(false);
    }
  }

  async function togglePin(note: CaseNote) {
    try {
      await updateCaseNote(caseId, note.id, { is_pinned: !note.isPinned });
      await refetch();
    } catch {
      toast.error("Couldn't update note.");
    }
  }

  async function saveEdit(note: CaseNote) {
    const text = editText.trim();
    if (!text) return;
    try {
      await updateCaseNote(caseId, note.id, { text });
      setEditingId(null);
      await refetch();
    } catch {
      toast.error("Couldn't update note.");
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteCaseNote(caseId, deleting.id);
      await refetch();
      onActivity?.();
    } catch {
      toast.error("Couldn't remove note.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <div className="grid gap-4">
      <form
        className="grid gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void addNote();
        }}
      >
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a note…"
          aria-label="New note"
          rows={2}
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={saving || !draft.trim()}>
            Add Note
          </Button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-muted-foreground text-sm">No notes or activity yet.</p>
      ) : (
        <ul className="grid gap-2">
          {notes.map((note) =>
            note.isSystem ? (
              <li
                key={note.id}
                className="text-muted-foreground flex items-center gap-2 px-1 py-1.5 text-xs"
              >
                <History className="size-3.5 shrink-0" />
                <span className="italic">{note.text}</span>
                <span className="ml-auto shrink-0">{formatWhen(note.createdAt)}</span>
              </li>
            ) : (
              <li
                key={note.id}
                className={cn(
                  "bg-card rounded-md border p-3",
                  note.isPinned && "border-primary/40 bg-primary/5",
                )}
              >
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-foreground text-xs font-semibold">
                    {note.authorName || "Unknown"}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {formatWhen(note.createdAt)}
                  </span>
                  {note.isPinned && (
                    <span className="text-primary text-[10px] font-bold uppercase">
                      Pinned
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={note.isPinned ? "Unpin note" : "Pin note"}
                      onClick={() => void togglePin(note)}
                    >
                      {note.isPinned ? (
                        <PinOff className="size-4" />
                      ) : (
                        <Pin className="size-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Edit note"
                      onClick={() => {
                        setEditingId(note.id);
                        setEditText(note.text);
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete note"
                      onClick={() => setDeleting(note)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  </div>
                </div>
                {editingId === note.id ? (
                  <div className="grid gap-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      aria-label="Edit note text"
                      rows={2}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        disabled={!editText.trim()}
                        onClick={() => void saveEdit(note)}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-foreground text-sm whitespace-pre-wrap">
                    {note.text}
                  </p>
                )}
              </li>
            ),
          )}
        </ul>
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete note?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the note from the case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
